import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';

// Define allowed roles
type Role = 'superadmin' | 'admin' | 'gm' | 'domain_user' | 'artist' | 'guest' | 'team' | 'judge';

interface WithAuthProps {
  allowedRoles: Role[];
}

const withAuth = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  { allowedRoles }: WithAuthProps
): React.FunctionComponent<P> => {
  const WithAuthComponent: React.FunctionComponent<P> = (props) => {
    const { user, isAuthenticated, loading } = useAuth();
    const [isChecking, setIsChecking] = useState(true);
    
    
    useEffect(() => {
      // Wait for the main auth loading to complete
      if (loading) {
        return;
      }
      
      // No need for additional delay, proceed immediately
      setIsChecking(false);
    }, [loading, user, isAuthenticated]);
    
    // Show loading state while checking auth
    if (loading || isChecking) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      );
    }
    
    // Not authenticated
    if (!isAuthenticated || !user) {
      return <Navigate to="/login" replace />;
    }
    
    // Check role — superadmin passes all route guards
    const roleIsAllowed = user.role === 'superadmin' || allowedRoles.includes(user.role as Role);

    if (roleIsAllowed) {
      return <WrappedComponent {...props} />;
    }
    
    // If the user is authenticated but doesn't have the required role
    return <Navigate to="/unauthorized" replace />;
  };
  
  return WithAuthComponent;
};

export default withAuth;